import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import type { FormateurTarifsDraft } from '../../../utils/formateurTarifsDraft';

type Props = {
  value: FormateurTarifsDraft;
  onChange: (next: FormateurTarifsDraft) => void;
  onSave?: () => void;
  saving?: boolean;
  disabled?: boolean;
};

const fieldClass =
  'academia-field w-full rounded-lg border border-outline-variant/25 bg-white px-3 py-2 text-sm tabular-nums text-slate-900 shadow-sm transition focus:border-primary/40 focus:ring-2 focus:ring-primary/15';

export default function FormateurTarifsEditor({
  value,
  onChange,
  onSave,
  saving = false,
  disabled = false,
}: Props) {
  const update = (patch: Partial<FormateurTarifsDraft>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4 rounded-2xl border border-outline-variant/20 bg-emerald-50/40 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LucideSymbolIcon name="payments" tone="primary" size={20} />
          </span>
          <div className="min-w-0">
            <h4 className="text-sm font-black text-slate-900">Rémunération de ce formateur</h4>
            <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">
              Par défaut la grille globale (Administration → Tarification) s’applique. Activez pour fixer un tarif
              horaire et une prime transport spécifiques.
            </p>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-outline-variant/20 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
            checked={value.enabled}
            disabled={disabled || saving}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          Rémunération personnalisée
        </label>
      </div>

      {!value.enabled ? (
        <p className="rounded-xl border border-dashed border-outline-variant/30 bg-white/80 px-4 py-3 text-sm text-on-surface-variant">
          Ce formateur utilise les montants définis dans <strong>Tarification des frais</strong> (onglet tarifs
          formateur).
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Tarif horaire (CDF)</span>
            <input
              type="number"
              min={0}
              step="1"
              className={fieldClass}
              value={value.montantHoraireCdf}
              disabled={disabled || saving}
              onChange={(e) => update({ montantHoraireCdf: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Réf. USD horaire</span>
            <input
              type="number"
              min={0}
              step="0.01"
              className={fieldClass}
              value={value.referenceUsdHoraire}
              disabled={disabled || saving}
              placeholder="optionnel"
              onChange={(e) => update({ referenceUsdHoraire: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Prime transport / jour (CDF)</span>
            <input
              type="number"
              min={0}
              step="1"
              className={fieldClass}
              value={value.primeTransportJourCdf}
              disabled={disabled || saving}
              onChange={(e) => update({ primeTransportJourCdf: e.target.value })}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Réf. USD transport</span>
            <input
              type="number"
              min={0}
              step="0.01"
              className={fieldClass}
              value={value.referenceUsdTransport}
              disabled={disabled || saving}
              placeholder="optionnel"
              onChange={(e) => update({ referenceUsdTransport: e.target.value })}
            />
          </label>
        </div>
      )}

      {onSave ? (
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-primary/90 disabled:opacity-50"
            disabled={disabled || saving}
            onClick={onSave}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer la rémunération'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
